import type { Curve, CurvePath, Object3D } from 'three'
import { BufferGeometry, CatmullRomCurve3, Clock, Line, LineBasicMaterial, PerspectiveCamera, Vector3 } from 'three'
import * as TWEEN from '@tweenjs/tween.js'
import type SceneControl from '../core/scene/scene'

export enum Direction {
  plus = 'plus',
  minus = 'minus',
}

/**
 * moveTo 直线移动
 * @param currentObject3D
 * @param targetObject3D
 * @param distance
 * @param direction
 * @param duration
 * @param animationMethod
 * @param sceneControl
 */
export const moveTo = (currentObject3D: Object3D, targetObject3D: Object3D, distance: number, direction: Direction, duration?: number, animationMethod?: string, sceneControl?: SceneControl) => {
  const targetPosition = targetObject3D.position
  const cameraPosition = currentObject3D.position

  // 单位方向向量
  let toward = targetPosition.clone().sub(cameraPosition).normalize()

  if (direction === Direction.minus)
    toward = toward.negate()

  const target = cameraPosition.clone().add(toward.multiplyScalar(distance))

  const totalDistance = cameraPosition.distanceTo(targetPosition)
  const currentDistance = cameraPosition.distanceTo(target)

  let lastPosition = target.clone()

  if (direction === Direction.plus && currentDistance > totalDistance)
    lastPosition = targetPosition

  const tween = new TWEEN.Tween(cameraPosition)

  tween.to(lastPosition, duration || 1000)
  tween.start()

  if (currentObject3D instanceof PerspectiveCamera && sceneControl) {
    sceneControl.context.globalControl.add(() => {
      currentObject3D.lookAt(targetPosition)
    }, (duration || 1000) * 1.01)

    sceneControl.context.globalControl.start()
  }

  return tween
}

/**
 * create line
 * @param points
 * @returns
 */
const createLine = (points: Vector3[]) => {
  const curve = new CatmullRomCurve3([
    ...points,
  ])

  const curvePoints = curve.getPoints(50)
  const geometry = new BufferGeometry().setFromPoints(curvePoints)
  const material = new LineBasicMaterial({ opacity: 1 })
  return [new Line(geometry, material), curve] as [Line, CatmullRomCurve3]
}

export interface ControlsRetuenFuncType {
  setSpeed: (currentStep: number) => void
  getProgress: () => number
  setProgress: (newProgress: number) => void
  stop: () => void
  continue: (newProgress?: number) => void
  remove: () => void
  addEventListener: (event: 'end', callback: () => void) => void
  getWalkCurve: () => CatmullRomCurve3 | null
}

type MoveWithLineOptions = {
  lookat?: Vector3 | boolean
  loop?: boolean
  repeat?: number
  groundCurve?: Curve<Vector3>
  lookatObject?: Object3D
  splitCount?: number
  duration?: number
  speed?: number
  yoyo?: boolean
  paused?: boolean
  direction?: number
  sceneControl?: SceneControl
}
/**
 * 曲线路径移动
 * @param currentObject
 * @param curve
 * @param speed
 * @param options
 */
export const moveWithLine = (currentObject: Object3D, curve: Curve<Vector3> | CurvePath<Vector3>, options?: MoveWithLineOptions) => {
  const {
    lookat,
    loop = true,
    repeat = -1,
    groundCurve,
    lookatObject,
    duration,
    speed = 1,
    yoyo = false,
    paused = false,
    direction = 1,
    sceneControl,
  } = options || {}

  if (!repeat)
    return

  let currentLookAt = lookat
  let currentLookatObject = lookatObject

  const clock = new Clock()
  const eventListenerMap = new Map<string, Set<() => void>>()
  const currentCurve: Curve<Vector3> | CurvePath<Vector3> = curve.clone()
  const curveLength = currentCurve.getLength()

  const currentGroundCurve = groundCurve || currentCurve

  const allPoints = currentGroundCurve.getSpacedPoints(10000).map(point => new Vector3(point.x, point.y + 3.2, point.z))

  let [elapsed, runCount, currentSpeed, curDirection] = [0, 0, speed, direction]
  // 如果传递的是总时长，计算运行的速率
  if (duration)
    currentSpeed = curveLength / duration

  const handleCallback = () => {
    const onComplete = () => {
      runCount++
      if (repeat !== -1 && runCount >= repeat) {
        if (eventListenerMap.has('end'))
          eventListenerMap.get('end')?.forEach(callback => callback())

        sceneControl?.context.globalControl.remove(handleCallback)
        return true
      }
      return false
    }
    elapsed += clock.getDelta() * currentSpeed * curDirection

    let t = (elapsed / curveLength)

    if (yoyo) {
      if (t >= 1) {
        if (onComplete())
          return
        t = 1
        curDirection = -1 // 反向
        elapsed = curveLength // 保持在曲线的末端
      }
      else if (t < 0) {
        if (onComplete())
          return
        t = 0
        curDirection = 1 // 正向
        elapsed = 0 // 保持在曲线的起点
      }
    }
    else {
      if (t >= 1) {
        if (onComplete())
          return
        t = 0
        elapsed = 0
      }
      else if (t <= 0) {
        if (onComplete())
          return
        t = 1
        elapsed = curveLength
      }
    }

    const nextPoint = currentCurve.getPointAt(t)

    currentObject.position.set(nextPoint.x, nextPoint.y, nextPoint.z)

    const newObject = currentLookatObject ?? currentObject
    if (currentLookAt) {
      newObject.lookAt(currentLookAt as Vector3)
    }
    else if (currentLookAt !== false) {
      if (t >= 1 && repeat >= runCount)
        newObject.lookAt(currentCurve.getPointAt(t))
      else
        newObject.lookAt(currentCurve.getPointAt(Math.max(0, Math.min(t + 0.001 * direction, 1))))
    }
  }

  if (sceneControl) {
    sceneControl.context.globalControl.add(handleCallback, Infinity)
    paused !== true && sceneControl.context.globalControl.start()
  }

  return {
    setLookAt: (lookat: Vector3 | boolean) => {
      currentLookAt = lookat
    },

    setLooKAtObject: (object: Object3D) => {
      currentLookatObject = object
    },

    setSpeed: (newSpeed: number) => {
      currentSpeed = newSpeed
    },

    getWalkCurve: () => {
      return new CatmullRomCurve3(allPoints.slice(0, Math.floor(elapsed / curveLength * 10000)))
    },

    stop: () => {
      sceneControl?.context.globalControl.remove(handleCallback)
      clock.stop()
    },

    continue: () => {
      sceneControl?.context.globalControl.remove(handleCallback)
      sceneControl?.context.globalControl.add(handleCallback, Infinity)

      if (!clock.running)
        clock.start()

      sceneControl?.context.globalControl.start()
    },

    restart: () => {
      elapsed = 0
      runCount = 0
      if (sceneControl) {
        sceneControl.context.globalControl.add(handleCallback, Infinity)
        clock.start()
        sceneControl.context.globalControl.start()
      }
    },

    getProgress: () => elapsed / curveLength,

    setProgress: (newProgress: number) => {
      elapsed = curveLength * newProgress
    },

    addEventListener: (event: 'end', callback: () => void) => {
      if (eventListenerMap.has(event))
        eventListenerMap.get(event)?.add(callback)
      else
        eventListenerMap.set(event, new Set([callback]))
    },

    remove: () => sceneControl?.context.globalControl.remove(handleCallback),
  } as ControlsRetuenFuncType
}

export const moveWithLine2 = (currentObject: Object3D, curve: Curve<Vector3>, curSpeed = 1, options?: MoveWithLineOptions) => {
  const {
    lookat,
    loop = true,
    groundCurve,
    lookatObject,
    splitCount = 10000,
    sceneControl,
  } = options || {}

  let currentLookAt = lookat
  let currentLookatObject = lookatObject

  const clock = new Clock()
  const eventListenerMap = new Map<string, Set<() => void>>()
  const currentCurve = curve.clone()
  const curveLength = currentCurve.getLength()
  const currentGroundCurve = groundCurve || currentCurve
  const allPoints = currentGroundCurve.getSpacedPoints(splitCount).map(point => new Vector3(point.x, point.y + 3.2, point.z))

  let elapsed = 0
  let speed = curSpeed

  const handleCallback = () => {
    elapsed += clock.getDelta() * speed

    const t = (elapsed / curveLength)

    if (t >= 1) {
      if (!loop) {
        if (eventListenerMap.has('end'))
          eventListenerMap.get('end')?.forEach(callback => callback())

        return sceneControl?.context.globalControl.remove(handleCallback)
      }
      else {
        elapsed = 0
      }
    }

    const nextPoint = currentCurve.getPointAt(t % 1)

    currentObject.position.set(nextPoint.x, nextPoint.y, nextPoint.z)

    const newObject = currentLookatObject ?? currentObject
    if (currentLookAt) {
      newObject.lookAt(currentLookAt as Vector3)
    }
    else if (currentLookAt !== false) {
      if (t >= 1 && !loop)
        newObject.lookAt(currentCurve.getPointAt(t % 1))
      else
        newObject.lookAt(currentCurve.getPointAt(Math.min(t % 1 + 0.01, 1)))
    }
  }

  if (sceneControl) {
    sceneControl.context.globalControl.add(handleCallback, Infinity)
    sceneControl.context.globalControl.start()
  }

  return {
    setLookAt: (lookat: Vector3 | boolean) => {
      currentLookAt = lookat
    },

    setLooKAtObject: (object: Object3D) => {
      currentLookatObject = object
    },

    setSpeed: (newSpeed: number) => {
      speed = newSpeed
    },

    getWalkCurve: () => {
      return new CatmullRomCurve3(allPoints.slice(0, Math.floor(elapsed / curveLength * 10000)))
    },

    stop: () => {
      sceneControl?.context.globalControl.remove(handleCallback)
      clock.stop()
    },

    continue: () => {
      sceneControl?.context.globalControl.remove(handleCallback)
      sceneControl?.context.globalControl.add(handleCallback, Infinity)

      if (!clock.running)
        clock.start()

      sceneControl?.context.globalControl.start()
    },

    getProgress: () => elapsed / curveLength,

    setProgress: (newProgress: number) => {
      elapsed = curveLength * newProgress
    },

    addEventListener: (event: 'end', callback: () => void) => {
      if (eventListenerMap.has(event))
        eventListenerMap.get(event)?.add(callback)
      else
        eventListenerMap.set(event, new Set([callback]))
    },

    remove: () => sceneControl?.context.globalControl.remove(handleCallback),
  } as ControlsRetuenFuncType
}

/**
 * base 圆周运动
 * @param currentObject3d
 * @param speed
 * @param duration
 */
export const moveWithRound = (currentObject3d: Object3D, speed = 0.1, duration?: number, sceneControl?: SceneControl) => {
  let angle = 0
  let x = 0
  let z = 0

  const handleCallback = () => {
    angle += speed
    x = 4 * Math.sin(angle)
    z = 4 * Math.cos(angle)

    currentObject3d.position.set(x, 0, z)
  }

  if (sceneControl) {
    sceneControl.context.globalControl.add(handleCallback, duration)
    sceneControl.context.globalControl.start()
  }
}

/**
 * move 方法2 移动辅助线
 * @param currentObject3D
 * @param targetObject3D
 */
export const moveLine = (currentObject3D: Object3D, targetObject3D: Object3D, sceneControl?: SceneControl) => {
  const [curveObject, curve] = createLine([
    currentObject3D.position,
    targetObject3D.position,
  ])

  sceneControl?.context.scene.add(curveObject)
}